<template>
  <div class="matching-viz">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>公式：</strong>f: A → B (一一对应) | 二分图最大匹配$</div>
    </div>

    <div class="main-layout vertical">
      <div class="canvas-box simulation">
        <svg width="100%" height="240" viewBox="0 0 500 240">
           <!-- Potential Links (Background) -->
           <g stroke="rgba(255, 255, 255, 0.05)" stroke-width="1.5">
             <line v-for="(edge, idx) in validEdges" :key="`bg_link_${idx}`"
                   :x1="edge.x1" :y1="edge.y1" :x2="edge.x2" :y2="edge.y2" />
           </g>

           <!-- Active Matched Links -->
           <g stroke="#00d2ff" stroke-width="3" stroke-linecap="round" filter="url(#glow-matching)">
             <line v-for="(match, i) in matches" :key="`match_${i}`"
                   :x1="nodesA[match.a].x" :y1="nodesA[match.a].y" 
                   :x2="nodesB[match.b].x" :y2="nodesB[match.b].y" />
           </g>

           <!-- Nodes A -->
           <g v-for="(node, i) in nodesA" :key="`A_${i}`">
             <circle :cx="node.x" :cy="node.y" r="12" :fill="matches.some(m => m.a === i) ? '#00d2ff' : '#222'" stroke="#00d2ff" stroke-width="2"/>
             <text :x="node.x" :y="node.y + 4" fill="#fff" font-size="12" text-anchor="middle" font-weight="bold">{{ i + 1 }}</text>
           </g>

           <!-- Nodes B -->
           <g v-for="(node, j) in nodesB" :key="`B_${j}`">
             <circle :cx="node.x" :cy="node.y" r="12" :fill="matches.some(m => m.b === j) ? '#ff4757' : '#222'" stroke="#ff4757" stroke-width="2"/>
             <text :x="node.x" :y="node.y + 4" fill="#fff" font-size="12" text-anchor="middle" font-weight="bold">{{ String.fromCharCode(65 + j) }}</text>
           </g>
        </svg>

        <div class="hud-top">
           <div class="stat">
              <label>图象与性质: 连通二部图寻找最优连线</label>
           </div>
        </div>
      </div>

      <div class="bottom-controls">
        <div class="control-card glass">
           <div class="row items-center gap-4">
              <div class="flex-1">
                 <label>操作与演示</label>
                 <el-button type="primary" size="mini" @click="generateGraph">刷新随机拓扑网络</el-button>
                 <el-button type="success" size="mini" @click="solveMatching" :disabled="matched">寻找最优配对(动画)</el-button>
              </div>
           </div>
        </div>

        <div class="discovery-note" v-latex>
           <p><strong>数学释义：</strong>“配对”思想在组合数学和生活中非常重要。它可以是高斯计算等差数列和的<strong>首尾配对</strong>，也可以是图论中的<strong>二分图匹配问题</strong>。这里演示了在一组不同的元素之间（集合A与集合B），寻找没有冲突的“连线”，也就是“最大匹配（Maximum Matching）”。</p>
        </div>
      </div>
    </div>

    <!-- Defs -->
    <svg height="0" width="0">
       <defs>
          <filter id="glow-matching">
             <feGaussianBlur stdDeviation="1.5" result="blur" />
             <feMerge> <feMergeNode in="blur" /> <feMergeNode in="SourceGraphic" /> </feMerge>
          </filter>
       </defs>
    </svg>
  </div>
</template>

<script>
export default {
  name: 'MatchingViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      numNodes: 6,
      adjacency: [],
      matches: [], // Array of {a, b}
      matched: false,
      isAnimating: false
    }
  },
  computed: {
    nodesA() {
      let arr = [];
      const startY = (240 - (this.numNodes - 1) * 35) / 2;
      for (let i = 0; i < this.numNodes; i++) {
        arr.push({ x: 120, y: startY + i * 35 });
      }
      return arr;
    },
    nodesB() {
      let arr = [];
      const startY = (240 - (this.numNodes - 1) * 35) / 2;
      for (let i = 0; i < this.numNodes; i++) {
        arr.push({ x: 380, y: startY + i * 35 });
      }
      return arr;
    },
    validEdges() {
      let edges = [];
      if (!this.adjacency || this.adjacency.length === 0) return edges;
      for (let i = 0; i < this.numNodes; i++) {
        for (let j = 0; j < this.numNodes; j++) {
           if (this.adjacency[i] && this.adjacency[i][j]) {
             edges.push({
                x1: this.nodesA[i].x, y1: this.nodesA[i].y,
                x2: this.nodesB[j].x, y2: this.nodesB[j].y
             });
           }
        }
      }
      return edges;
    }
  },
  methods: {
    generateGraph() {
      this.matched = false;
      this.matches = [];
      this.adjacency = [];
      for (let i = 0; i < this.numNodes; i++) {
        let edges = [];
        for (let j = 0; j < this.numNodes; j++) {
          edges.push(Math.random() > 0.4);
        }
        this.adjacency.push(edges);
      }
    },
    canMatch(i, j) {
      if (!this.adjacency[i]) return false;
      return this.adjacency[i][j];
    },
    // Simple greedy matching for animation purposes
    solveMatching() {
      if (this.matched || this.isAnimating) return;
      this.isAnimating = true;
      let matchedA = new Set();
      let matchedB = new Set();
      let currentA = 0;

      const step = () => {
        if (currentA >= this.numNodes) {
          this.matched = true;
          this.isAnimating = false;
          return;
        }

        if (!matchedA.has(currentA)) {
          for (let j = 0; j < this.numNodes; j++) {
            if (this.canMatch(currentA, j) && !matchedB.has(j)) {
              this.matches.push({ a: currentA, b: j });
              matchedA.add(currentA);
              matchedB.add(j);
              break;
            }
          }
        }
        currentA++;
        setTimeout(step, 200);
      };

      step();
    }
  },
  mounted() {
    this.generateGraph();
  }
}
</script>

<style scoped lang="scss">
.matching-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.canvas-box { background: #010205; border-radius: 12px; position: relative; border: 1px solid rgba(255,255,255,0.05); }
.bottom-controls { flex: 0.6; display: flex; flex-direction: column; gap: 10px; overflow-y: auto; }
.control-card { background: rgba(255,255,255,0.02); border-radius: 12px; padding: 15px; }
.discovery-note { background: rgba(0, 210, 255, 0.05); padding: 12px 18px; border-radius: 12px; p { margin: 0; font-size: 0.75rem; color: #aaa; line-height: 1.5; strong { color: #fff; } } }
.row { display: flex; gap: 20px; align-items: center; }
label { font-size: 0.75rem; color: #888; margin-bottom: 5px; display: block; }
.flex-1 { flex: 1; }
</style>
